import { useEffect } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/app/store';
import { playNotificationSound } from '@/utils/notificationSound';
import { useSettings } from '@/features/shared/settings/hooks/useSettings';

/**
 * Hook to handle payment-related notifications
 * Shows in-app notifications for payment events and invalidates React Query cache
 *
 * Events handled:
 * - payment:created: New payment received from bank
 * - payment:allocated: Payment assigned to affaire(s)
 * - payment:validated: Payment validation status changed
 * - payment:accepted: Over/underpayment accepted
 */
export const usePaymentNotifications = () => {
  const { socket, isConnected } = useSocket();
  const queryClient = useQueryClient();
  const addNotification = useAppStore(state => state.addNotification);
  const { settings } = useSettings();

  useEffect(() => {
    if (!socket || !isConnected) {
      console.log('[PaymentNotifications] Socket not available or not connected');
      return;
    }

    /**
     * Handle new payment created event
     */
    const handlePaymentCreated = (data: any) => {
      console.log('[PaymentNotifications] Received payment:created event:', data);

      const amount = parseFloat(data.payment?.amount || 0).toFixed(2);
      const debtor = data.payment?.debtor_name || 'Inconnu';

      // Play notification sound if enabled (default to true if settings not loaded)
      if (settings?.soundNotificationsEnabled !== false) {
        playNotificationSound();
      }

      // Show in-app notification
      addNotification({
        type: 'info',
        title: 'Nouveau paiement',
        message: `€${amount} de ${debtor}`
      });

      // Invalidate payment queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    };

    /**
     * Handle payment allocation event
     */
    const handlePaymentAllocated = (data: any) => {
      console.log('[PaymentNotifications] Received payment:allocated event:', data);

      const affaireCount = data.affaires?.length || 0;
      const allocator = data.allocatedBy?.name || 'Inconnu';

      // Play notification sound if enabled (default to true if settings not loaded)
      if (settings?.soundNotificationsEnabled !== false) {
        playNotificationSound();
      }

      // Show in-app notification
      addNotification({
        type: 'success',
        title: 'Paiement alloué',
        message: `${allocator} a alloué le paiement à ${affaireCount} affaire(s)`
      });

      // Invalidate payment queries
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    };

    /**
     * Handle payment validation event
     */
    const handlePaymentValidated = (data: any) => {
      console.log('[PaymentNotifications] Received payment:validated event:', data);

      const affaireId = data.affaire_id || '';
      const validator = data.validatedBy?.name || 'Inconnu';
      const isValidated = data.validated;

      // Play notification sound if enabled (default to true if settings not loaded)
      if (settings?.soundNotificationsEnabled !== false) {
        playNotificationSound();
      }

      // Show in-app notification
      addNotification({
        type: isValidated ? 'success' : 'info',
        title: isValidated ? 'Paiement validé' : 'Validation annulée',
        message: `Affaire ${affaireId} par ${validator}`
      });

      // Invalidate payment queries
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    };

    /**
     * Handle payment acceptance event (over/underpayment)
     */
    const handlePaymentAccepted = (data: any) => {
      console.log('[PaymentNotifications] Received payment:accepted event:', data);

      const affaireId = data.affaire_id || '';
      const acceptor = data.acceptedBy?.name || 'Inconnu';

      // Play notification sound if enabled (default to true if settings not loaded)
      if (settings?.soundNotificationsEnabled !== false) {
        playNotificationSound();
      }

      // Show in-app notification
      addNotification({
        type: 'success',
        title: 'Paiement accepté',
        message: `Affaire ${affaireId} acceptée par ${acceptor}`
      });

      // Invalidate payment queries
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    };

    // Register event listeners
    socket.on('payment:created', handlePaymentCreated);
    socket.on('payment:allocated', handlePaymentAllocated);
    socket.on('payment:validated', handlePaymentValidated);
    socket.on('payment:accepted', handlePaymentAccepted);

    console.log('[PaymentNotifications] WebSocket listeners attached');

    // Cleanup
    return () => {
      socket.off('payment:created', handlePaymentCreated);
      socket.off('payment:allocated', handlePaymentAllocated);
      socket.off('payment:validated', handlePaymentValidated);
      socket.off('payment:accepted', handlePaymentAccepted);
      console.log('[PaymentNotifications] WebSocket listeners removed');
    };
  }, [socket, isConnected, queryClient, addNotification]);
};
